import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Modal } from 'app-components';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { ROOT_STATE } from '@store/store.types';
import DatePicker from '../../../../lib/inputs/react-datepicker';
import Dropdown from '../../../../lib/inputs/Dropdown';
import CurrencyInput from '../../../../components/common/form/CurrencyInput';
import CommonTextArea from '../../common/input/CommonTextArea';
import {
    EXCHANGE_ELIGIBILITY_OPTIONS,
    OVERRIDE_PROGRAM_ELIGIBILITY_TABLE_HEADER_DATA,
    CSR_ELIGIBILITY_OPTIONS,
    INPUT_MAX_LENGTH,
    ConditionalRendering,
    ValidationErrorMessage,
    INPUT_TYPES,
    ICsrOverideProgramEligibilityModalProps,
    GIButton,
    DEFAULT_PERSON_ID,
    APPLICANT_ELIGIBILITY_TYPE,
    SOPA_OPTIONS,
    getAllowedBhpOptions,
    isStateSubsidyDisabled,
} from '../../utils';
import useOverrideProgramEligibility from '../../hooks/useOverrideProgramEligibility';
import {
    ModalContainer,
    StyledModal,
    CommonData,
    Label,
    StyledField,
    OverrideTable,
    Td,
    StyledDropdown,
    InputContainer,
    StyledNumberInput,
} from './styles/CsrOverrideProgramEligibilityModal.styled';


const CsrOverrideProgramEligibilityModal = ({
    closeModal,
    data,
    application,
}: ICsrOverideProgramEligibilityModalProps) => {
    const stateCode = useSelector((state: ROOT_STATE) => state.common.environment.stateCode);
    const isState = useSelector((state: ROOT_STATE) => state.common.environment.isState);

    const {
        overrideEligibilityData,
        householdStateSubsidyAmountValue,
        maximumAPTCValue,
        error,
        confirmationText,
        setConfirmationText,
        isModalPhaseOverride,
        isFinancialAssistanceFlag,
        isStateSubsidyOverrideEnabled,
        isBhpOverrideEnabled,
        hasPremiumAssistanceSubsidyFields,
        originalBhpTypes,
        handleChangeTableData,
        handleDateChange,
        handleHouseholdSubsidyChange,
        handleHouseholdSubsidyBlur,
        handleMaximumAPTCChange,
        handleMaximumAPTCBlur,
        onSubmit,
        onBack,
        onClose,
    } = useOverrideProgramEligibility({
        data,
        stateCode,
        application,
        closeModal,
    });

    return (
        <ModalContainer>
            <Modal id="override_program_eligibility_modal" classNames="maxw-none">
                <Modal.Header
                    id="override_program_eligibility_modal_title"
                    titleId={
                        isModalPhaseOverride
                            ? 'cap.applications.modal.overrideProgramEligibility.title'
                            : 'cap.applications.modal.overrideProgramEligibility.title2'
                    }
                    onHide={onClose}
                />
                <Modal.Body>
                    <ConditionalRendering isTrue={isModalPhaseOverride}>
                        <p data-testid="override_program_eligibility_modal_body_text">
                            <FormattedMessage id="cap.application.modal.overrideProgramEligibilityAlert.text" />
                        </p>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={!isModalPhaseOverride}>
                        <p data-testid="override_program_eligibility_modal_body_text">
                            <FormattedMessage id="cap.application.modal.confirmProgramEligibilityAlert.text" />
                        </p>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={error.hasBhpEligibilityError}>    
                        <AlertMessage content={<FormattedMessage id="cap.application.modal.overrideProgramEligibility.notEligibleBhp" />} type={'error'} showIcon={true} icon='exclamation-circle' />
                    </ConditionalRendering>
                    <CommonData>
                        <ConditionalRendering isTrue={isFinancialAssistanceFlag}>
                            <div className="w-50p">
                                <StyledNumberInput>
                                    <Label htmlFor='household_maximum_aptc_amount_input' data-testid="household_maximum_aptc_amount_label">
                                        <FormattedMessage id="cap.application.modal.overrideProgramEligibility.commonData.label.householdMaximumAPTCAmount" />
                                    </Label>
                                    <CurrencyInput
                                        input={{
                                            id: 'household_maximum_aptc_amount_input',
                                            name: 'household_maximum_aptc_amount_input',
                                            value: maximumAPTCValue,
                                            inputclasses: [],
                                            isDisabled: !isModalPhaseOverride,
                                        }}
                                        actions={{
                                            handleChange: handleMaximumAPTCChange,
                                            handleBlur: handleMaximumAPTCBlur,
                                        }}
                                        label={{
                                            labelText: '',
                                            labelClasses: [],
                                            showRequired: false,
                                            wrapperClasses: ['mt-0'],
                                        }}
                                        maskOptions={{
                                            includeThousandsSeparator: false,
                                        }}
                                        inputPaddingLeft='2'
                                        maxWidth='20rem'
                                        preserveDollarOnBlur={true}
                                        showIcon={false}
                                        hasLargeInput={true}
                                        isStepArrowsVisible={false}
                                    />
                                    <ValidationErrorMessage
                                        errorClasses={['text-14', 'margin-0']}
                                        isVisible={error.aptcMaxAmntErrorFlag}
                                        messageText={
                                            'cap.application.modal.programEligibilityEnterDate.maxValidAptcAmount'
                                        }
                                    />
                                    <ValidationErrorMessage
                                        errorClasses={['text-14', 'margin-0']}
                                        isVisible={error.aptcZeroAmntErrorFlag}
                                        messageText={
                                            'cap.application.modal.programEligibilityEnterDate.aptAmountShouldBeZero'
                                        }
                                    />
                                </StyledNumberInput>
                            </div>
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={isStateSubsidyOverrideEnabled}>
                            <div className="w-50p">
                                <StyledNumberInput>
                                    <Label htmlFor='household_state_subsidy_amount' data-testid="household_state_subsidy_amount_label">
                                        <FormattedMessage id="cap.application.modal.overrideProgramEligibility.commonData.label.householdStateSubsidyAmount" />
                                    </Label>
                                    <CurrencyInput
                                        input={{
                                            id: 'household_state_subsidy_amount',
                                            name: 'household_state_subsidy_amount',
                                            value: householdStateSubsidyAmountValue,
                                            inputclasses: [],
                                            isDisabled: !isModalPhaseOverride
                                        }}
                                        actions={{
                                            handleChange: handleHouseholdSubsidyChange,
                                            handleBlur: handleHouseholdSubsidyBlur,
                                        }}
                                        label={{
                                            labelText: '',
                                            labelClasses: [],
                                            showRequired: false,
                                            ariaLabel: 'Household state subsidy amount',
                                            wrapperClasses: ['mt-0'],
                                        }}
                                        maskOptions={{
                                            includeThousandsSeparator: false,
                                        }}
                                        inputPaddingLeft='2'
                                        maxWidth='20rem'
                                        preserveDollarOnBlur={true}
                                        showIcon={false}
                                        hasLargeInput={true}
                                        isStepArrowsVisible={false}
                                    />
                                    <ValidationErrorMessage
                                        errorClasses={['text-14', 'margin-0']}
                                        isVisible={error.hasHouseHoldStateSubsidyAmountInvalidError}
                                        messageText={
                                            'cap.application.modal.overrideProgramEligibility.invalidHouseholdStateSubsidyAmount'
                                        }
                                    />
                                    <ValidationErrorMessage
                                        errorClasses={['text-14', 'margin-0']}
                                        isVisible={error.hasHouseHoldStateSubsidyAmountNotEligibleError}
                                        messageText={
                                            'cap.application.modal.overrideProgramEligibility.notEligibleHouseholdStateSubsidyAmount'
                                        }
                                    />
                                </StyledNumberInput>
                            </div>
                        </ConditionalRendering>
                        <ConditionalRendering isTrue={!isState.NJ}>
                            <div className="w-50p">
                                <Label htmlFor='dob' data-testid="eligibility_label">
                                    <FormattedMessage id="cap.application.modal.overrideProgramEligibility.commonData.label.eligibilityStartDate" />
                                </Label>
                                <div>
                                    <StyledField>
                                        <DatePicker
                                            labelText=""
                                            wrapperClasses={['date-picker']}
                                            isRequired
                                            id="dob"
                                            type="text"
                                            pattern="MM/DD/YYYY"
                                            disabled={!isModalPhaseOverride}
                                            value={overrideEligibilityData?.eligibilityStartDate}
                                            withMask={true}
                                            onDateChange={value => handleDateChange(value || '')}
                                        />
                                        <ValidationErrorMessage
                                            errorClasses={['text-14', 'margin-0']}
                                            isVisible={!overrideEligibilityData?.eligibilityStartDate}
                                            messageText={
                                                'cap.application.modal.programEligibilityEnterDate.enterEligibilityStartDate'
                                            }
                                        />
                                        <ValidationErrorMessage
                                            errorClasses={['text-14', 'margin-0']}
                                            isVisible={
                                                overrideEligibilityData?.eligibilityStartDate && error.invalidStartDate
                                            }
                                            messageText={
                                                'cap.application.modal.programEligibilityEnterDate.enterValidStartDate'
                                            }
                                        />
                                        <ValidationErrorMessage
                                            errorClasses={['text-14', 'margin-0']}
                                            isVisible={
                                                overrideEligibilityData?.eligibilityStartDate &&
                                                !error.invalidStartDate &&
                                                error?.differentYear
                                            }
                                            messageText={
                                                'cap.application.modal.programEligibilityEnterDate.sepEnrollmentPeriods'
                                            }
                                            textReplacements={application.coverageYear}
                                        />
                                    </StyledField>
                                </div>
                            </div>
                        </ConditionalRendering>
                    </CommonData>
                    <OverrideTable>
                        <tr>
                            {OVERRIDE_PROGRAM_ELIGIBILITY_TABLE_HEADER_DATA.map((item) => {
                                let shouldRender = true;
                                if (item.renderConditionally) {
                                    if (item.id === 'bhp') {
                                        shouldRender = isBhpOverrideEnabled;
                                    } else if (item.id === 'stateSubsidy') {
                                        shouldRender = isStateSubsidyOverrideEnabled && !hasPremiumAssistanceSubsidyFields;
                                    } else if (item.id === 'nmpa' || item.id === 'napa' || item.id === 'sopa') {
                                        shouldRender = isStateSubsidyOverrideEnabled && hasPremiumAssistanceSubsidyFields;
                                    } else {
                                        shouldRender = isFinancialAssistanceFlag;
                                    }
                                }
                                return (
                                    <ConditionalRendering isTrue={shouldRender} key={item.id}>
                                        <th className="pr-10" data-testid={item.id}>
                                            <FormattedMessage id={item.text} />
                                        </th>
                                    </ConditionalRendering>
                                );
                            })}
                        </tr>
                        {overrideEligibilityData.applicantEligibility.map((applicant) => {
                            return (
                                <tr key={`applicant_key_${applicant.id}`}>
                                    <Td data-testid={`applicant_name_${applicant.id}`}>
                                        {`${applicant.firstName} ${applicant.middleName ? applicant.middleName : ''} ${
                                            applicant.lastName
                                        }`}
                                        <ConditionalRendering isTrue={applicant.personId === DEFAULT_PERSON_ID}>
                                            {' '}
                                            (<FormattedMessage id={'cap.applications.modal.primaryText'} />)
                                        </ConditionalRendering>
                                    </Td>
                                    <Td>
                                        <ConditionalRendering isTrue={isModalPhaseOverride}>
                                            <StyledDropdown
                                                width={'110px'}
                                                data-testid={`exchange_eligibility_dropdown_${applicant.id}`}
                                            >
                                                <Dropdown
                                                    shouldParse={true}
                                                    label={{
                                                        labelText: '',
                                                    }}
                                                    inputData={{
                                                        inputClasses: ['gi-dropdown__select grid-col-4'],
                                                        name: 'exchange_eligibility',
                                                        options: EXCHANGE_ELIGIBILITY_OPTIONS,
                                                        type: INPUT_TYPES.DROPDOWN,
                                                        value: applicant.exchangeEligibility,
                                                        isInputRequired: false,
                                                    }}
                                                    inputActions={{
                                                        handleFieldChange: val => {
                                                            handleChangeTableData(applicant.id, APPLICANT_ELIGIBILITY_TYPE.EXCHANGE_ELIGIBILITY, val);
                                                        },
                                                    }}
                                                />
                                            </StyledDropdown>
                                        </ConditionalRendering>
                                        <ConditionalRendering isTrue={!isModalPhaseOverride}>
                                            <div className="p-14-0">{applicant.exchangeEligibility}</div>
                                        </ConditionalRendering>
                                    </Td>
                                    <ConditionalRendering isTrue={isFinancialAssistanceFlag}>
                                        <Td>
                                            <ConditionalRendering isTrue={isModalPhaseOverride}>
                                                <StyledDropdown
                                                    width={'110px'}
                                                    data-testid={`aptc_eligibility_dropdown_${applicant.id}`}
                                                >
                                                    <Dropdown
                                                        shouldParse={true}
                                                        label={{
                                                            labelText: '',
                                                            labelClass: [''],
                                                        }}
                                                        inputData={{
                                                            inputClasses: ['gi-dropdown__select grid-col-4'],
                                                            name: 'aptc_eligibility',
                                                            options: EXCHANGE_ELIGIBILITY_OPTIONS,
                                                            type: INPUT_TYPES.DROPDOWN,
                                                            value: applicant.aptcEligibility,
                                                            isInputRequired: false,
                                                            disabled: isStateSubsidyDisabled(applicant)
                                                        }}
                                                        inputActions={{
                                                            handleFieldChange: val => {
                                                                handleChangeTableData(applicant.id, APPLICANT_ELIGIBILITY_TYPE.APTC_ELIGIBILITY, val);
                                                            },
                                                        }}
                                                    />
                                                </StyledDropdown>
                                            </ConditionalRendering>
                                            <ConditionalRendering isTrue={!isModalPhaseOverride}>
                                                <div className="p-14-0">{applicant.aptcEligibility}</div>
                                            </ConditionalRendering>
                                        </Td>
                                    </ConditionalRendering>
                                    <Td>
                                        <ConditionalRendering isTrue={isModalPhaseOverride}>
                                            <StyledDropdown
                                                width={'160px'}
                                                data-testid={`csr_eligibility_dropdown_${applicant.id}`}
                                            >
                                                <Dropdown
                                                    label={{
                                                        labelText: '',
                                                    }}
                                                    inputData={{
                                                        inputClasses: ['gi-dropdown__select grid-col-4'],
                                                        name: 'csr_eligibility',
                                                        options: Object.values(CSR_ELIGIBILITY_OPTIONS).map(v => v),
                                                        type: INPUT_TYPES.DROPDOWN,
                                                        value: applicant.csrLevel,
                                                        isInputRequired: false,
                                                        disabled: isStateSubsidyDisabled(applicant)
                                                    }}
                                                    inputActions={{
                                                        handleFieldChange: val => {
                                                            handleChangeTableData(applicant.id, APPLICANT_ELIGIBILITY_TYPE.CSR_LEVEL, val);
                                                        },
                                                    }}
                                                />
                                            </StyledDropdown>
                                        </ConditionalRendering>
                                        <ConditionalRendering isTrue={!isModalPhaseOverride}>
                                            <div className="p-14-0">
                                                {CSR_ELIGIBILITY_OPTIONS[applicant.csrLevel]?.text}
                                            </div>
                                        </ConditionalRendering>
                                    </Td>
                                    <ConditionalRendering isTrue={isStateSubsidyOverrideEnabled}>
                                        <ConditionalRendering isTrue={!hasPremiumAssistanceSubsidyFields}>
                                            <Td>
                                                <ConditionalRendering isTrue={isModalPhaseOverride}>
                                                    <StyledDropdown
                                                        width={'110px'}
                                                        data-testid={`state_subsidy_dropdown_${applicant.id}`}
                                                    >
                                                        <Dropdown
                                                            shouldParse={true}
                                                            label={{
                                                                labelText: '',
                                                            }}
                                                            inputData={{
                                                                inputClasses: ['gi-dropdown__select grid-col-4'],
                                                                name: 'state_subsidy_dropdown',
                                                                options: EXCHANGE_ELIGIBILITY_OPTIONS,
                                                                type: INPUT_TYPES.DROPDOWN,
                                                                value: applicant.stateSubsidyEligibility,
                                                                isInputRequired: false,
                                                                disabled: isStateSubsidyDisabled(applicant),
                                                            }}
                                                            inputActions={{
                                                                handleFieldChange: val => {
                                                                    handleChangeTableData(applicant.id, APPLICANT_ELIGIBILITY_TYPE.STATE_SUBSIDY_ELIGIBILITY, val);
                                                                },
                                                            }}
                                                        />
                                                    </StyledDropdown>
                                                </ConditionalRendering>
                                                <ConditionalRendering isTrue={!isModalPhaseOverride}>
                                                    <div className="p-14-0">
                                                        {applicant.stateSubsidyEligibility}
                                                    </div>
                                                </ConditionalRendering>
                                            </Td>
                                        </ConditionalRendering>
                                        <ConditionalRendering isTrue={hasPremiumAssistanceSubsidyFields}>
                                            <ConditionalRendering isTrue={isModalPhaseOverride}>
                                                <Td>
                                                    <StyledDropdown
                                                        width={'110px'}
                                                        data-testid={`state_subsidy_nmpa_dropdown_${applicant.id}`}
                                                    >
                                                        <Dropdown
                                                            shouldParse={true}
                                                            label={{
                                                                labelText: '',
                                                            }}
                                                            inputData={{
                                                                inputClasses: ['gi-dropdown__select grid-col-4'],
                                                                name: 'state_subsidy_nmpa_dropdown',
                                                                options: EXCHANGE_ELIGIBILITY_OPTIONS,
                                                                type: INPUT_TYPES.DROPDOWN,
                                                                value: applicant.newMexicoPremiumAssistance,
                                                                isInputRequired: false,
                                                                disabled: isStateSubsidyDisabled(applicant),
                                                            }}
                                                            inputActions={{
                                                                handleFieldChange: val => {
                                                                    handleChangeTableData(applicant.id, APPLICANT_ELIGIBILITY_TYPE.NMPA, val);
                                                                },
                                                            }}
                                                        />
                                                    </StyledDropdown>
                                                </Td>
                                                <Td>
                                                    <StyledDropdown
                                                        width={'110px'}
                                                        data-testid={`state_subsidy_napa_dropdown_${applicant.id}`}
                                                    >
                                                        <Dropdown
                                                            shouldParse={true}
                                                            label={{
                                                                labelText: '',
                                                            }}
                                                            inputData={{
                                                                inputClasses: ['gi-dropdown__select grid-col-4'],
                                                                name: 'state_subsidy_napa_dropdown',
                                                                options: EXCHANGE_ELIGIBILITY_OPTIONS,
                                                                type: INPUT_TYPES.DROPDOWN,
                                                                value: applicant.nativeAmericanPremiumAssistance,
                                                                isInputRequired: false,
                                                                disabled: isStateSubsidyDisabled(applicant),
                                                            }}
                                                            inputActions={{
                                                                handleFieldChange: val => {
                                                                    handleChangeTableData(applicant.id, APPLICANT_ELIGIBILITY_TYPE.NAPA, val);
                                                                },
                                                            }}
                                                        />
                                                    </StyledDropdown>
                                                </Td>
                                                <Td>
                                                    <StyledDropdown
                                                        width={'110px'}
                                                        data-testid={`state_subsidy_sopa_dropdown_${applicant.id}`}
                                                    >
                                                        <Dropdown
                                                            label={{
                                                                labelText: '',
                                                            }}
                                                            inputData={{
                                                                inputClasses: ['gi-dropdown__select grid-col-4'],
                                                                name: 'state_subsidy_sopa_dropdown',
                                                                options: SOPA_OPTIONS.map(v => ({
                                                                    value: v,
                                                                    text: v,
                                                                })),
                                                                type: INPUT_TYPES.DROPDOWN,
                                                                value: applicant.stateOutOfPocketAssistance,
                                                                isInputRequired: false,
                                                                disabled: isStateSubsidyDisabled(applicant),
                                                            }}
                                                            inputActions={{
                                                                handleFieldChange: val => {
                                                                    handleChangeTableData(applicant.id, APPLICANT_ELIGIBILITY_TYPE.SOPA, val);
                                                                },
                                                            }}
                                                        />
                                                    </StyledDropdown>
                                                </Td>
                                            </ConditionalRendering>
                                            <ConditionalRendering isTrue={!isModalPhaseOverride}>
                                                <Td>
                                                    <div className="p-14-0">
                                                        {applicant.newMexicoPremiumAssistance}
                                                    </div>
                                                </Td>
                                                <Td>
                                                    <div className="p-14-0">
                                                        {applicant.nativeAmericanPremiumAssistance}
                                                    </div>
                                                </Td>
                                                <Td>
                                                    <div className="p-14-0">
                                                        {applicant.stateOutOfPocketAssistance}
                                                    </div>
                                                </Td>
                                            </ConditionalRendering>
                                        </ConditionalRendering>
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={isBhpOverrideEnabled}>
                                        <Td>
                                            <ConditionalRendering isTrue={isModalPhaseOverride}>
                                                <StyledDropdown
                                                    width={'160px'}
                                                    data-testid={`bhp_eligibility_dropdown_${applicant.id}`}
                                                >
                                                    <Dropdown
                                                        shouldParse={true}
                                                        label={{
                                                            labelText: '',
                                                        }}
                                                        inputData={{
                                                            inputClasses: ['gi-dropdown__select grid-col-4'],
                                                            name: 'bhp_eligibility_dropdown',
                                                            options: getAllowedBhpOptions(originalBhpTypes[applicant.id]),
                                                            type: INPUT_TYPES.DROPDOWN,
                                                            value: applicant.bhpType,
                                                            isInputRequired: false,
                                                        }}
                                                        inputActions={{
                                                            handleFieldChange: val => {
                                                                handleChangeTableData(applicant.id, APPLICANT_ELIGIBILITY_TYPE.BHP_TYPE, val);
                                                            },
                                                        }}
                                                    />
                                                </StyledDropdown>
                                            </ConditionalRendering>
                                            <ConditionalRendering isTrue={!isModalPhaseOverride}>
                                                <div className="p-14-0">
                                                    {applicant.bhpType}
                                                </div>
                                            </ConditionalRendering>
                                        </Td>
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={isFinancialAssistanceFlag}>
                                        <Td data-testid={`medicaid_eligibility_dropdown_${applicant.id}`}>
                                            {applicant.medicaidEligibility}
                                        </Td>
                                    </ConditionalRendering>
                                    <ConditionalRendering isTrue={isFinancialAssistanceFlag}>
                                        <Td data-testid={`chip_eligibility_dropdown_${applicant.id}`}>
                                            {applicant.chipEligibility}
                                        </Td>
                                    </ConditionalRendering>
                                </tr>
                            );
                        })}
                    </OverrideTable>

                    <ConditionalRendering isTrue={!isModalPhaseOverride}>
                        <InputContainer>
                            <label>
                                <FormattedMessage id="cap.application.modal.overrideProgramEligibility.confirmationPhase.label" />
                                . <span>*</span>
                            </label>
                            <CommonTextArea
                                id="override_program_eligibility_modal_textarea"
                                value={confirmationText}
                                setValue={(value: string) =>
                                    value.length <= INPUT_MAX_LENGTH && setConfirmationText(value)
                                }
                                placeholder={'member.enrollment.modal.textarea.placeholder'}
                                style={{ height: '80px' }}
                            />
                            <div>
                                {INPUT_MAX_LENGTH - confirmationText.trim().length}{' '}
                                <FormattedMessage id="cap.applications.modal.charactersLeft" />
                            </div>
                        </InputContainer>
                    </ConditionalRendering>
                </Modal.Body>
                <Modal.Controls>
                    <StyledModal>
                        <div className="gap-2">
                            <ConditionalRendering isTrue={!isModalPhaseOverride}>
                                <GIButton
                                    id="override_program_eligibility_modal_back_button"
                                    labelId="cap.applications.modal.button.label.back"
                                    onClickHandler={onBack}
                                ></GIButton>
                            </ConditionalRendering>
                            <GIButton
                                id="override_program_eligibility_modal_continue_button"
                                labelId="button.override"
                                onClickHandler={onSubmit}
                                isDisabled={
                                    isModalPhaseOverride ? Object.values(error).some(Boolean) : !confirmationText.length
                                }
                            ></GIButton>
                            <GIButton
                                id="override_program_eligibility_modal_cancel_button"
                                labelId="cap.applications.modal.button.label.cancel"
                                variant="secondary"
                                onClickHandler={onClose}
                            ></GIButton>
                        </div>
                    </StyledModal>
                </Modal.Controls>
            </Modal>
        </ModalContainer>
    );
};

export default CsrOverrideProgramEligibilityModal;
